import { ParentTypes } from '@risksmart-app/domain/src/types/consts/parent-type';
import { CustomAttributeFieldType } from '@risksmart-app/form-configuration/src/field-types/types';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTRPC } from 'src/utils/trpc';

import type { AdditionalConditionField } from '@/components/form/edit-field-modal/formRegistryService';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { findT1Ancestor } from './getT1CategoryTitle';

/**
 * The rule's `propertyKey`. It stores the Tier-1 risk's **Id**, not its title:
 * titles change on rename, and `risk` has no unique index on Title (only
 * `(OrgKey, SequentialId)`), so two Tier-1 risks can legitimately share one and
 * a title-based rule would match records under both.
 */
export const T1_CATEGORY_FIELD_ID = 'T1CategoryRiskId';

type RiskRow = {
  Id: string;
  ParentRiskId?: null | string;
  Tier: number;
  Title: string;
};

/**
 * Declares "T1 Category" as a conditional-logic source on the Risk form, and
 * resolves the current record's value for it (CUS-242).
 *
 * T1 Category is not a form field — it is the nearest Tier-1 ancestor walking
 * `ParentRiskId` — so it needs both halves: a synthetic field declaration for
 * the editor picker and the runtime schema, and an externally-resolved value to
 * evaluate against.
 *
 * **V3 only.** The walk needs `Tier` and `ParentRiskId`, which only the tRPC
 * read returns (CUS-242 added them to `getRisksByTierQueryConfig`; the GraphQL
 * document deliberately does not carry them). Gating the picker alone would be a
 * trap — a rule authored on V3 still sits in `form_field_configuration`, and the
 * runtime graph would evaluate it on V2 with nothing to resolve against, hiding
 * a field for a reason no one can see. So both surfaces read this one gate.
 *
 * **Risk form only.** Only Risk has a T1 ancestor chain. Whether records merely
 * *linked* to a risk should offer it is an open product question on the ticket.
 */
export const useT1CategoryConditionSource = ({
  currentValues,
  formId,
  isEnabled: isEnabledOverride = true,
  label,
}: {
  /**
   * The record being edited. Omitted by the form editor, which needs the field
   * declaration but has no record to resolve — `conditionValues` is then
   * undefined and only `additionalField` is produced.
   */
  currentValues?: FieldValues;
  formId: null | string | undefined;
  /** Lets the editor avoid fetching while its dialog is closed. */
  isEnabled?: boolean;
  label: string;
}): {
  additionalField: AdditionalConditionField | undefined;
  conditionValues: Record<string, unknown> | undefined;
} => {
  const trpc = useTRPC();
  const isTrpcEnabled = useIsFeatureFlagEnabled('trpc');
  const isRiskForm = formId === ParentTypes.Risk;
  const isEnabled = isTrpcEnabled && isRiskForm && isEnabledOverride;

  // Read the four fields the walk needs individually. `watch()` returns a new
  // object every render, so depending on it would rebuild the risk Map and
  // re-walk the chain on every keystroke.
  const recordId = currentValues?.Id;
  const recordTitle = currentValues?.Title;
  const recordParentRiskId = currentValues?.ParentRiskId;
  const recordTier = Number(currentValues?.Tier);
  const hasRecord = !!currentValues;

  // Tier-1 risks are the selectable values, so they are fetched whenever the
  // source is offered at all. Tier 2 is only needed to walk a record that sits
  // below it, so it stays unfetched for the Tier-1 and Tier-2 records that make
  // up most forms.
  const { data: tierOneRisks } = useReactQuery({
    ...trpc.frontend.risk.risksByTier.queryOptions({ tier: 1 }),
    enabled: isEnabled,
  });

  const { data: tierTwoRisks } = useReactQuery({
    ...trpc.frontend.risk.risksByTier.queryOptions({ tier: 2 }),
    enabled: isEnabled && Number.isFinite(recordTier) && recordTier >= 3,
  });

  return useMemo(() => {
    if (!isEnabled) {
      return { additionalField: undefined, conditionValues: undefined };
    }

    const tierOne = (tierOneRisks ?? []) as RiskRow[];

    const additionalField: AdditionalConditionField = {
      key: T1_CATEGORY_FIELD_ID,
      label,
      // Single-select semantics: equals / not equals / any of / none of, the
      // same operator set every other enumerable source gets.
      type: CustomAttributeFieldType.Select,
      options: tierOne.map((risk) => ({ id: risk.Id, label: risk.Title })),
    };

    const riskById = new Map<string, RiskRow>(
      [...tierOne, ...((tierTwoRisks ?? []) as RiskRow[])].map((risk) => [
        risk.Id,
        risk,
      ])
    );

    if (!hasRecord) {
      return { additionalField, conditionValues: undefined };
    }

    const record = {
      Id: String(recordId ?? ''),
      ParentRiskId: (recordParentRiskId as null | string) ?? null,
      Tier: recordTier,
      Title: String(recordTitle ?? ''),
    };

    // An unresolved walk yields null rather than a sentinel, so the evaluator
    // finds nothing to match — the orphan behaviour the ticket specifies.
    //
    // "Orphaned" and "risks not yet fetched" are indistinguishable here, and
    // both resolve to null, so a gated field stays HIDDEN until the data lands
    // and then appears. That is deliberate: the opposite posture would briefly
    // render a field the org's rule says to hide, and flashing hidden content
    // is worse than a beat of delay before showing it.
    const ancestorId = Number.isFinite(record.Tier)
      ? findT1Ancestor(record, riskById)?.Id
      : undefined;

    return {
      additionalField,
      conditionValues: { [T1_CATEGORY_FIELD_ID]: ancestorId ?? null },
    };
  }, [
    isEnabled,
    label,
    tierOneRisks,
    tierTwoRisks,
    hasRecord,
    recordId,
    recordTitle,
    recordParentRiskId,
    recordTier,
  ]);
};
